import React, { useState, useEffect, useRef, useCallback } from 'react';
import TextComponent from '../shared/TextComponent';
import HeaderComponent from '../shared/HeaderComponent';
import FooterComponent from '../shared/FooterComponent';
import NavigationComponent from '../shared/NavigationComponent';
import DropdownComponent from '../shared/DropdownComponent';
import TextInputComponent from '../shared/TextInputComponent';
import PillComponent from '../shared/PillComponent';
import CheckboxComponent from '../shared/CheckboxComponent';
import BulletComponent from '../shared/BulletComponent';
import SubheaderComponent from '../shared/SubheaderComponent';
import DisclosureComponent from '../shared/DisclosureComponent';
import QuestionComponent from '../shared/QuestionComponent';
import PricingEstimateBox from '../shared/PricingEstimateBox';
import ErrorBoundary from '../shared/ErrorBoundary';
import LogoComponent from '../shared/LogoComponent';
import SeparatorComponent from '../shared/SeparatorComponent';
import {
  updateGTMResponse,
  pushSchedulerEntrySelect,
  pushSchedulerHandoffST,
  getAndIncrementStepIndex,
  resetStepIndex,
} from '../../helpers/GTMResponseBuilder';
import { updateServiceTitanResponse } from '../../helpers/STResponseBuilder';
import {
  addToBuffer,
  clearStEventBuffer,
  getBufferSnapshot,
} from './bufferStore';
import { SUPPORTED_FORMATS, DEFAULT_FORM_ID } from '../../helpers/constants';
import {
  pushSchedulerStep,
  pushSelectionEvent,
} from '../../helpers/GTMResponseBuilder';
import {
  normalize,
  isRequired,
  isMultiSelect,
  getQuestionText,
  getElementInfo,
  filterQuestionsByServiceType,
  filterQuestionsByPropertyType,
  isTextInputFormat,
  cleanBranchName,
  cleanedFormType,
  isPillMultiSelect,
  findParentQuestion,
  extractDNIPhoneFromPage,
  getPricingInfo,
  buildPricingPayload,
  getPricingDisplayInfo,
  filterQuestionsBySupportedFormats,
  togglePillValue,
  generateUUID,
  formatUSD,
} from '../../helpers/filtersAndHelpers';
import './enterpriseTool.css';
import '../shared/mobile.css';

// Map dropdown text to stable entry_key
function getEntryKey(entryLabel) {
  const mapping = {
    'Get pricing for a service or installation': 'get_pricing',
    'Sign up for a service membership': 'service_membership',
    'Troubleshoot an issue': 'troubleshoot',
    'Request emergency help': 'emergency_help',
    'Schedule an appointment': 'schedule_appointment',
    'Decide on repair vs. replacement': 'repair_vs_replacement',
    'Learn about a service or preventative maintenance': 'learn_about_service',
  };
  return (
    mapping[entryLabel] || entryLabel.toLowerCase().replace(/[^a-z0-9]+/g, '_')
  );
}

function inferMultiSelect(question) {
  if (!question) return false;
  const candidates = [
    'Multi',
    'Multi Select',
    'Allow Multiple',
    'Allow Multi',
    'Allow Multi Select',
    'Is Multi',
  ];
  return candidates.some((k) => {
    const val = question[k];
    if (val === true) return true;
    const s = String(val || '')
      .trim()
      .toLowerCase();
    return ['true', 'yes', '1', 'multi'].includes(s);
  });
}
function coerceId(id) {
  return id == null ? '' : String(id);
}

/**
 * Sorts a flat list of questions so that parent groups are kept together.
 * Algorithm:
 *  1. Identify "root" items — those whose Parent ID is not found within the
 *     current `items` list (they are top-level for this screen).
 *  2. For each root, compute an "anchor order": walk up the full question
 *     ancestry (using the optional `allQuestions` universe) to find the
 *     outermost ancestor's order. This ensures that when two roots both have
 *     out-of-list parents (e.g., they are children of two different pills
 *     selected on a previous screen), they are ordered by those pills' own
 *     order values rather than by the children's own (potentially swapped)
 *     order values.
 *  3. Sort roots by anchor order, then by their own order as a tiebreaker.
 *  4. After each root, recursively insert its in-list children sorted by
 *     order, then their children, etc.
 *
 * @param {Array}  items        - The flat list of questions to sort (current screen).
 * @param {Array}  [allQuestions] - The full question universe for ancestry lookup.
 */
function groupedSort(items, allQuestions) {
  if (!items || items.length === 0) return items;
  const idSet = new Set(items.map((q) => String(q.ID)));

  // Build a quick lookup map for the full question universe (if provided)
  const allById = {};
  if (Array.isArray(allQuestions)) {
    for (const q of allQuestions) {
      allById[String(q.ID)] = q;
    }
  }

  const byParent = {};
  const roots = [];

  for (const q of items) {
    const pid = q['Parent ID'];
    const parentInList = pid && pid !== '-' && idSet.has(String(pid));
    if (parentInList) {
      const key = String(pid);
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(q);
    } else {
      roots.push(q);
    }
  }

  // Compute the "anchor order" for a root item.
  // Always use the item's own order value. This is correct because:
  // - Parent pill order values are always numerically below their children's own
  //   order values (they are assigned in sequence in the same range).
  // - Using the parent's order as anchor caused parentless items (e.g. a "Benefits
  //   and Cost" header with no parent) to sort AFTER all pill-children, because
  //   the parentless item's own order (e.g. 81207) was higher than the pill parents'
  //   orders (~81075) but lower than the children's own orders (~81215+).
  // - Sorting roots by their own order naturally interleaves parentless items and
  //   pill-children correctly, since both are assigned order values in the same
  //   sequence within a given flow/screen.
  function anchorOrder(q) {
    return Number(q.order) || 0;
  }

  // Sort each group by order
  const byOrder = (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0);

  // Sort roots: primary key = anchor order (parent-chain order), secondary = own order
  roots.sort((a, b) => {
    const anchorA = anchorOrder(a);
    const anchorB = anchorOrder(b);
    if (anchorA !== anchorB) return anchorA - anchorB;
    return (Number(a.order) || 0) - (Number(b.order) || 0);
  });

  for (const key of Object.keys(byParent)) {
    byParent[key].sort(byOrder);
  }

  // Walk the tree and flatten in parent-first order
  function collect(id) {
    const result = [];
    const children = byParent[String(id)] || [];
    for (const child of children) {
      result.push(child);
      result.push(...collect(child.ID));
    }
    return result;
  }

  const output = [];
  for (const root of roots) {
    output.push(root);
    output.push(...collect(root.ID));
  }
  return output;
}

// Recursively render children of a question
function renderChildren({
  parentId,
  questions,
  selectedOptionId,
  childSelectedOptionIds,
  inputAnswers,
  handleMainPillSelect,
  handleChildPillSelect,
  handleInputChange,
  pillOptionsFn,
  childPillOptionsFn,
  childQuestionsFn,
  depth = 0,
  branchMetaObj,
  parentQuestion = null, // added parentQuestion param
}) {
  const children = childQuestionsFn(parentId);

  // Use shared SUPPORTED_FORMATS constant
  const supportedFormats = SUPPORTED_FORMATS;

  return children.map((child) => {
    const childFormat = normalize(child['Component/Format']);
    const childPills = childPillOptionsFn(child.ID);

    if (childFormat === 'separator') {
      return <SeparatorComponent key={child.ID} />;
    }

    if (!supportedFormats.includes(childFormat)) {
      return null;
    }

    if (childFormat === 'dropdown') {
      return (
        <DropdownComponent
          key={child.ID}
          question={child}
          value={inputAnswers[child.ID] || ''}
          onChange={(val, e) => handleInputChange(child.ID, val, e)}
        />
      );
    }
    if (isTextInputFormat(childFormat)) {
      return (
        <TextInputComponent
          key={child.ID}
          question={child}
          value={inputAnswers[child.ID] || ''}
          onChange={(val, e) => handleInputChange(child.ID, val, e)}
        />
      );
    }
    if (childFormat === 'checkbox') {
      return (
        <CheckboxComponent
          key={child.ID}
          question={child}
          checked={!!inputAnswers[child.ID]}
          onChange={(val, e) => handleInputChange(child.ID, val, e)}
        />
      );
    }
    if (childFormat === 'disclosure') {
      return (
        <DisclosureComponent
          key={child.ID}
          question={child}
          checked={!!inputAnswers[child.ID]}
          onChange={(val, e) => handleInputChange(child.ID, val, e)}
        />
      );
    }
    if (childFormat === 'pricing') {
      return (
        <ErrorBoundary>
          <PricingEstimateBox
            question={child}
            branchMetaObj={branchMetaObj}
            parentQuestion={parentQuestion}
          />
        </ErrorBoundary>
      );
    }
    if (childFormat === 'text') {
      return (
        <React.Fragment key={child.ID}>
          <TextComponent question={child} />
          {renderChildren({
            parentId: child.ID,
            questions,
            selectedOptionId,
            childSelectedOptionIds,
            inputAnswers,
            handleMainPillSelect,
            handleChildPillSelect,
            handleInputChange,
            pillOptionsFn,
            childPillOptionsFn,
            childQuestionsFn,
            depth: depth + 1,
            branchMetaObj,
            parentQuestion: child,
          })}
        </React.Fragment>
      );
    }
    if (childFormat === 'bullet') {
      return (
        <BulletComponent
          key={child.ID}
          question={child}
          parentQuestion={parentQuestion}
          branchMetadata={branchMetaObj}
        />
      );
    }
    if (childFormat === 'subheader') {
      return (
        <SubheaderComponent key={child.ID} question={child} depth={depth}>
          {renderChildren({
            parentId: child.ID,
            questions,
            selectedOptionId,
            childSelectedOptionIds,
            inputAnswers,
            handleMainPillSelect,
            handleChildPillSelect,
            handleInputChange,
            pillOptionsFn,
            childPillOptionsFn,
            childQuestionsFn,
            depth: depth + 1,
            branchMetaObj,
            parentQuestion: child, // propagate
          })}
        </SubheaderComponent>
      );
    }
    if (childFormat === 'question') {
      return (
        <QuestionComponent key={child.ID} question={child} depth={depth}>
          {childPills.length > 0 && (
            <div className="pill-options-group">
              {childPills.map((option) => {
                const childMultiCombined =
                  isMultiSelect(child) || inferMultiSelect(child);
                const childSelectedRaw = childSelectedOptionIds[child.ID];
                const isChildSelected = childMultiCombined
                  ? Array.isArray(childSelectedRaw)
                    ? childSelectedRaw
                        .map(coerceId)
                        .includes(coerceId(option.ID))
                    : false
                  : coerceId(childSelectedRaw) === coerceId(option.ID);
                return (
                  <button
                    key={option.ID}
                    className={`pill-btn${isChildSelected ? ' selected' : ''}`}
                    onClick={(e) =>
                      handleChildPillSelect(child.ID, option.ID, e)
                    }
                    type="button"
                  >
                    <PillComponent
                      question={option}
                      label={getQuestionText(option)}
                    />
                  </button>
                );
              })}
            </div>
          )}
          {renderChildren({
            parentId: child.ID,
            questions,
            selectedOptionId,
            childSelectedOptionIds,
            inputAnswers,
            handleMainPillSelect,
            handleChildPillSelect,
            handleInputChange,
            pillOptionsFn,
            childPillOptionsFn,
            childQuestionsFn,
            depth: depth + 1,
            branchMetaObj,
            parentQuestion: child, // propagate
          })}
        </QuestionComponent>
      );
    }
    return null;
  });
}

let enterpriseToolMountCount = 0;
// UUID generator
function EnterpriseTool({
  isOpen = true,
  resetSignal,
  formType: propFormType,
  entryName: propEntryName,
  environment: propEnvironment,
}) {
  enterpriseToolMountCount += 1;
  const [flowId, setFlowId] = useState(generateUUID());
  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    resetStepIndex();
    return () => {
      // Removed unmount debug log
    };
  }, []);
  useEffect(() => {}, [propEntryName]);
  // Persist stResponseRef globally to survive remounts
  if (!window.__stResponseObj) {
    window.__stResponseObj = { events: [] };
  }
  const stResponseRef = useRef(window.__stResponseObj);
  const [questions, setQuestions] = useState(null);
  const [allQuestions, setAllQuestions] = useState(null); // full set (flow scoped)
  const [derivedFormId, setDerivedFormId] = useState(null); // chosen from first screen question (HVAC/Plumbing/Electrical/Generator)
  const [formIdQuestionId, setFormIdQuestionId] = useState(null); // id of the question offering HVAC/ELECTRICAL/PLUBMING/GENERATOR
  const [branchMetadata, setBranchMetadata] = useState(null); // restored branch metadata state
  const [branchServices, setBranchServices] = useState([]); // track services separately
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [screenNumber, setScreenNumber] = useState(1);
  const [selectedOptionId, setSelectedOptionId] = useState({});
  const [childSelectedOptionIds, setChildSelectedOptionIds] = useState({});
  const [inputAnswers, setInputAnswers] = useState({});
  const [lastScreenNumber, setLastScreenNumber] = useState(null);
  const [responseObj, setResponseObj] = useState({});
  const [stResponseObj, setSTResponseObj] = useState({ events: [] });
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  const resetCountRef = useRef(0);
  const stSummaryRef = useRef(''); // holds summary built when scheduler opens
  const processedJobIdsRef = useRef(new Set()); // renamed from processedAppointmentsRef
  const dniPhoneNumberRef = useRef(null); // stores DNI-swapped phone number

  // Diagnostic logger disabled
  const etLog = useCallback(() => {}, []);

  // Updated resetAllState with reason + logging
  const resetAllState = useCallback(
    (reason = 'unspecified') => {
      resetStepIndex();
      resetCountRef.current += 1;
      etLog('resetAllState', {
        reason,
        count: resetCountRef.current,
        screenNumber,
      });
      setScreenNumber(1);
      setSelectedOptionId({});
      setChildSelectedOptionIds({});
      setInputAnswers({});
      setResponseObj({});
      setSTResponseObj({ events: [] }); // ensure events array exists after reset
      setSelectedPropertyType(null);
    },
    [screenNumber, etLog]
  );

  // Sync stResponseRef with latest stResponseObj + debug
  useEffect(() => {
    stResponseRef.current = stResponseObj || { events: [] };
    // Removed stResponseObj sync debug log
  }, [stResponseObj]);

  // Handle modal open/close and flowId generation
  useEffect(() => {
    if (!prevIsOpenRef.current && isOpen) {
      const newFlowId = generateUUID();
      setFlowId(newFlowId);
      // Fire scheduler_entry_select event when tool is loaded/opened
      pushSchedulerEntrySelect({
        entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
        entry_key: getEntryKey(propEntryName || ''),
        entry_type: 'modal',
        flow_id: newFlowId,
      });
      resetAllState('modal-open');
    }
    if (prevIsOpenRef.current && !isOpen) {
      etLog('Modal closing detected');
      resetAllState('modal-close');
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, resetAllState, etLog, propEntryName]);

  // External reset via prop
  useEffect(() => {
    if (resetSignal !== undefined) {
      etLog('resetSignal trigger', { resetSignal });
      resetAllState('reset-signal');
    }
  }, [resetSignal, resetAllState, etLog]);

  // Custom event listener
  useEffect(() => {
    const handler = () => {
      etLog('EnterpriseToolModalClosed event');
      resetAllState('custom-event');
    };
    etLog('Adding EnterpriseToolModalClosed listener');
    window.addEventListener('EnterpriseToolModalClosed', handler);
    return () => {
      etLog('Removing EnterpriseToolModalClosed listener');
      window.removeEventListener('EnterpriseToolModalClosed', handler);
    };
  }, [resetAllState, etLog]);

  // Expose global helper
  useEffect(() => {
    etLog('Assigning window.resetEnterpriseTool');
    window.resetEnterpriseTool = (reason) => {
      etLog('window.resetEnterpriseTool invoked', { reason });
      resetAllState(reason || 'global-call');
    };
    return () => {
      if (window.resetEnterpriseTool) {
        etLog('Cleaning window.resetEnterpriseTool');
        delete window.resetEnterpriseTool;
      }
    };
  }, [resetAllState, etLog]);

  // Log screen changes
  useEffect(() => {
    etLog('screenNumber change', { screenNumber });
  }, [screenNumber, etLog]);

  // Extract props from rootDiv attributes at the top level so they are always defined
  const rootDiv = document.getElementById('questionnaire-form-root');
  // Use propFormType first, then fallback to DOM attribute
  const formType =
    propFormType || rootDiv?.getAttribute('data-form-type') || '';
  const branchName = rootDiv?.getAttribute('data-branch-name') || '';
  const formId = rootDiv?.getAttribute('data-form-id') || '';
  // Use propEnvironment first, then fallback to DOM attribute, then default to 'production'
  const environment =
    propEnvironment ||
    rootDiv?.getAttribute('data-environment') ||
    'production';

  // Modal element helper and MutationObserver fallback to ensure reset on close even if no event fired
  const getModalElement = useCallback(() => {
    if (!formType) return null;
    return document.getElementById(`form-modal-${formType}`);
  }, [formType]);

  useEffect(() => {
    const modalEl = getModalElement();
    if (!modalEl || typeof MutationObserver === 'undefined') {
      etLog('MutationObserver skipped', { hasModal: !!modalEl });
      return;
    }
    etLog('MutationObserver attach', { id: modalEl.id });
    const checkHidden = (hint) => {
      try {
        const display = getComputedStyle(modalEl).display;
        if (display === 'none') {
          etLog('MutationObserver detected hidden modal', { hint, display });
          resetAllState('mutation-observer-close');
        }
      } catch (e) {}
    };
    const observer = new MutationObserver((mutList) => {
      if (mutList.some((m) => m.type === 'attributes')) {
        checkHidden('attributes');
      }
    });
    observer.observe(modalEl, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    checkHidden('initial');
    return () => {
      etLog('MutationObserver disconnect', { id: modalEl.id });
      observer.disconnect();
    };
  }, [getModalElement, resetAllState, etLog]);

  // Internal interval visibility watcher fallback
  const lastVisibilityRef = useRef(null);
  useEffect(() => {
    const modalEl = getModalElement();
    if (!modalEl) return;
    etLog('visibilityWatcher init', { id: modalEl.id });
    const check = () => {
      try {
        const display = getComputedStyle(modalEl).display;
        const visible = display !== 'none';
        const last = lastVisibilityRef.current;
        if (last === null) {
          lastVisibilityRef.current = visible;
          return;
        }
        if (last !== visible) {
          etLog('visibility change detected', { from: last, to: visible });
          lastVisibilityRef.current = visible;
          if (!visible) {
            resetAllState('visibility-hidden');
          } else {
            resetAllState('visibility-shown');
          }
        }
      } catch (e) {}
    };
    const interval = setInterval(check, 600);
    return () => clearInterval(interval);
  }, [getModalElement, resetAllState, etLog]);

  // Fire scheduler_step when screen changes
  useEffect(() => {
    if (screenNumber > 0) {
      pushSchedulerStep({
        stepLabel: `Step: ${screenNumber}`,
        scheduler_name: branchName
          ? `${branchName} - Booking Form: ${formType}`
          : formType,
        flow_id: flowId,
        entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
        entry_key: getEntryKey(propEntryName || ''),
        step_index: getAndIncrementStepIndex(),
        question_id: 'screen_' + screenNumber,
        question_text: `Step: ${screenNumber}`,
      });
    }
  }, [screenNumber, branchName, formType, flowId, propEntryName]);

  // Fetch questions and branch specific information
  const logoUrl = window.QuestionnaireFormData?.logoUrl || '';
  const phoneNumber = window.QuestionnaireFormData?.phoneNumber || '';
  const tenantId = window.QuestionnaireFormData?.tenantId || '';
  useEffect(() => {
    let abort = false;
    async function fetchQuestionsAndMetadata(reason = 'initial') {
      try {
        setLoadingQuestions(true);
        etLog('Fetch start', { formType, reason });
        const apiBaseUrl = window.QuestionnaireFormData.apiBaseUrl;
        const frontendStart = Date.now();

        // Fetch ALL questions for this formType (not filtered by formId)
        const questionsUrl = `${apiBaseUrl}/proxyFormQuestions?formId=ALL&flow=${encodeURIComponent(cleanedFormType(formType))}&frontendTimestamp=${frontendStart}&environment=${encodeURIComponent(environment)}`;
        const branchMetadataUrl = `${apiBaseUrl}/proxyBranchMetadata?BRANCH_NAME=${encodeURIComponent(cleanBranchName(branchName))}&environment=${encodeURIComponent(environment)}`;

        const [questionsRes, branchMetadataRes] = await Promise.all([
          fetch(questionsUrl),
          fetch(branchMetadataUrl),
        ]);
        if (abort) return;
        if (!questionsRes.ok)
          throw new Error(`Questions API error: ${questionsRes.status}`);
        if (!branchMetadataRes.ok)
          throw new Error(
            `Branch Metadata API error: ${branchMetadataRes.status}`
          );
        const questionsData = await questionsRes.json();
        const branchMetadataData = await branchMetadataRes.json();

        // Log API responses
        console.log('[API Response] Questions:', {
          url: questionsUrl,
          metadata: questionsData.metadata,
          questionCount: questionsData.questions?.length || 0,
          sampleQuestions: questionsData.questions?.slice(0, 3) || [],
        });

        console.log('[API Response] Branch Metadata:', {
          url: branchMetadataUrl,
          resultCount: branchMetadataData.branchMetadata?.length || 0,
          branchMetadata: branchMetadataData.branchMetadata,
        });

        const branchServicesFetched =
          Array.isArray(branchMetadataData.branchMetadata) &&
          branchMetadataData.branchMetadata.length > 0
            ? branchMetadataData.branchMetadata[0].services
            : [];
        const rawQuestionsAll = Array.isArray(questionsData.questions)
          ? questionsData.questions.filter(
              (q) => !formType || normalize(q.Flow) === normalize(formType)
            )
          : [];
        // Deduplicate by ID — keep the record with the highest order value (most recently renumbered)
        const dedupeMap = new Map();
        for (const q of rawQuestionsAll) {
          const key = String(q.ID);
          const existing = dedupeMap.get(key);
          if (
            !existing ||
            (Number(q.order) || 0) > (Number(existing.order) || 0)
          ) {
            dedupeMap.set(key, q);
          }
        }
        const rawQuestions = Array.from(dedupeMap.values());
        if (rawQuestionsAll.length !== rawQuestions.length) {
          console.warn(
            `[Questions] Deduplicated ${rawQuestionsAll.length - rawQuestions.length} duplicate ID(s) from fetched questions`
          );
        }
        etLog('Fetch complete (all questions for formType)', {
          formType,
          count: rawQuestions.length,
          branchServices: branchServicesFetched.length,
        });
        setAllQuestions(rawQuestions);
        setBranchServices(branchServicesFetched);
        // Detect selector question/header within this partition (first screen).
        // Supports both Question and Header formats (e.g. Emergency flow uses Header).
        // Requires at least 3 service-type pills (HVAC, Electrical/Generator, Plumbing)
        // so that flows with a missing pill (e.g. Emergency missing Plumbing) still resolve.
        // NOTE: 'plubming' typo intentionally removed — only 'plumbing' is valid.
        try {
          const requiredTokens = [
            'hvac',
            'electrical',
            'plumbing',
            'generator',
          ];
          const SELECTOR_FORMATS = ['question', 'header'];
          const candidate = rawQuestions.find((q) => {
            if (Number(q['Screen Number']) !== 1) return false;
            if (!SELECTOR_FORMATS.includes(normalize(q['Component/Format'])))
              return false;
            const pillChildren = rawQuestions.filter(
              (c) =>
                normalize(c['Component/Format']) === 'pill' &&
                normalize(c['Parent ID']) === normalize(q.ID)
            );
            // Need at least 3 service-type pills (allows flows where one service is absent)
            if (pillChildren.length < 3) return false;
            const childTexts = pillChildren.map((c) =>
              (c.Text || '').trim().toLowerCase()
            );
            // At least 3 of the 4 required tokens must be present
            const matchCount = requiredTokens.filter((tok) =>
              childTexts.includes(tok)
            ).length;
            return matchCount >= 3;
          });
          if (candidate) setFormIdQuestionId(candidate.ID);
        } catch (e) {}
        setBranchMetadata(
          Array.isArray(branchMetadataData.branchMetadata)
            ? branchMetadataData.branchMetadata
            : []
        );
        setLastScreenNumber(
          rawQuestions.reduce((m, q) => {
            const sn = Number(q['Screen Number']);
            return isNaN(sn) ? m : Math.max(m, sn);
          }, 1)
        );
        setScreenNumber(1); // restart at first screen after any fetch
        // Clear previous answers except derivedFormId (we keep selection)
        setSelectedOptionId({});
        setChildSelectedOptionIds({});
        setInputAnswers({});
      } catch (e) {
        if (!abort) {
          etLog('Fetch error', { message: e.message });
          setAllQuestions([]);
          setBranchMetadata([]);
          setLastScreenNumber(1);
        }
      } finally {
        if (!abort) setLoadingQuestions(false);
      }
    }
    fetchQuestionsAndMetadata();
    return () => {
      abort = true;
    };
  }, [formType, branchName, environment]); // Filter questions by service type (formId) and property type
  useEffect(() => {
    if (!allQuestions) {
      setQuestions(allQuestions);
      return;
    }

    console.log('[Question Filtering] Stage 1 - All questions:', {
      count: allQuestions.length,
      screenNumber,
      selectedPropertyType,
      derivedFormId,
      allQuestions: allQuestions, // Full question objects
    });

    let working = [...allQuestions];

    // Filter by service type (HVAC/Plumbing/Electrical/Generator) after screen 1
    if (derivedFormId && screenNumber > 1) {
      console.log(
        '[Question Filtering] Stage 2 - Before service type filter:',
        {
          count: working.length,
          serviceType: derivedFormId,
          questions: working,
        }
      );

      working = working.filter((q) => {
        const qFormId = normalize(q.formId);
        const normalizedDerivedFormId = normalize(derivedFormId);

        // Include questions with no formId or matching formId
        if (
          !qFormId ||
          qFormId === '-' ||
          qFormId === normalizedDerivedFormId
        ) {
          return true;
        }
        return false;
      });

      console.log('[Question Filtering] Stage 3 - After service type filter:', {
        count: working.length,
        filtered: allQuestions.length - working.length,
        remainingQuestions: working,
      });
    }

    // Filter by property type after screen 1
    if (selectedPropertyType && screenNumber > 1) {
      console.log(
        '[Question Filtering] Stage 4 - Before property type filter:',
        {
          count: working.length,
          propertyType: selectedPropertyType,
          questions: working,
        }
      );

      working = filterQuestionsByPropertyType(working, [selectedPropertyType]);

      console.log(
        '[Question Filtering] Stage 5 - After property type filter:',
        {
          count: working.length,
          filtered: allQuestions.length - working.length,
          remainingQuestions: working,
        }
      );
    }

    console.log('[Question Filtering] Stage 6 - Final questions set:', {
      count: working.length,
      screenNumber,
      finalQuestions: working,
    });

    setQuestions(working);
  }, [allQuestions, derivedFormId, selectedPropertyType, screenNumber]);

  // Reset to screen 1 when derivedFormId first chosen
  useEffect(() => {
    if (derivedFormId) setScreenNumber(1);
  }, [derivedFormId]);

  // Listen for ServiceTitan BookingBooked postMessage events (enhanced verbose handler)
  // This useEffect calls the API (updateServiceTitanEvents) to update booking summary
  useEffect(() => {
    const handler = (event) => {
      try {
        const data = event?.data;
        if (data == null) return; // nothing further

        // Detect booking event
        let isBooking = false;
        let jobId = '';
        // Only set jobId from BookingBooked event data
        if (data && typeof data === 'object') {
          if (data.type === 'BookingBooked') {
            isBooking = true;
            jobId = data.appointmentId || data.id;
          } else if (
            data.type === 'se-send-client-google-analytics' &&
            data.ga?.action === 'BookingBooked'
          ) {
            isBooking = true;
            jobId = data.ga.value;
          }
        }
        if (!isBooking || !jobId) return;
        if (processedJobIdsRef.current.has(jobId)) return;
        processedJobIdsRef.current.add(jobId);
        pushSelectionEvent({
          event: 'scheduler_booking_booked',
          value: String(jobId || ''),
          selectedLabel: '',
          questionText: 'Booking Confirmed',
          type: 'iframe',
          scheduler_name: 'ServiceTitan',
          elementTag: 'iframe',
          flow_id: flowId || '',
          entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
        });
        const apiBaseUrl = window.QuestionnaireFormData.apiBaseUrl;
        const fnUrl = `${apiBaseUrl}/updateServiceTitanEvents`;
        const usedSummary =
          stSummaryRef.current ||
          JSON.stringify(window.__stSimpleAnswers || {});
        const payload = {
          tenant: tenantId,
          jobId, // ONLY from BookingBooked event
          summaryPayload: JSON.parse(usedSummary || '{}'),
        };
        console.log(
          '[ST Payload] updateServiceTitanEvents — final payload being sent to API:',
          {
            url: fnUrl,
            tenant: payload.tenant,
            jobId: payload.jobId,
            summaryPayload: payload.summaryPayload,
            rawSummaryString: usedSummary,
            fullSTResponseObj: window.__stResponseObj,
          }
        );
        fetch(fnUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        })
          .then((r) =>
            r
              .json()
              .catch(() => ({}))
              .then((j) => ({ ok: r.ok, status: r.status, body: j }))
          )
          .then((res) => {
            if (!res.ok) {
              console.error(
                '[EnterpriseTool] updateServiceTitanEvents error',
                res
              );
            }
          })
          .catch((err) =>
            console.error(
              '[EnterpriseTool] updateServiceTitanEvents fetch failed',
              err
            )
          );
      } catch (e) {
        console.warn('[EnterpriseTool] BookingBooked listener error', e);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [tenantId]);

  // Filter out pills from screenQuestions - they're only shown as options for their parent
  const screenQuestions = Array.isArray(questions)
    ? questions
        .filter(
          (q) =>
            Number(q['Screen Number']) === screenNumber &&
            normalize(q.ID) !== null &&
            normalize(q['Component/Format']) !== 'pill'
        )
        .sort((a, b) => {
          const orderA = Number(a.order) || 0;
          const orderB = Number(b.order) || 0;
          return orderA - orderB;
        })
    : [];

  console.log('[Screen Questions] Filtered for current screen:', {
    screenNumber,
    totalQuestions: questions?.length || 0,
    screenQuestionsCount: screenQuestions.length,
    fullScreenQuestions: screenQuestions, // Full question objects
  });

  // Get all selected pill IDs across all screens (for parent-child relationships)
  const selectedPillIdsAllScreens = Object.values(selectedOptionId)
    .flat()
    .filter(Boolean)
    .map(String);

  // Get all selected child pill IDs across all screens (stored separately in childSelectedOptionIds)
  const selectedChildPillIdsAllScreens = Object.values(childSelectedOptionIds)
    .flat()
    .filter(Boolean)
    .map(String);

  // Main pill options helper.
  // NOTE: The service selector question (formIdQuestionId) has pill children like
  // "HVAC", "Plumbing", "Electrical", "Generator" whose Service Type values are plain
  // slugs ("hvac", "plumbing", etc.) that match branchServices. These SHOULD be filtered
  // so that a branch offering only HVAC doesn't show Plumbing/Electrical/Generator pills
  // on Screen 1. The isServiceSelector flag is intentionally NOT used to bypass filtering
  // anymore — the service selector pills benefit from branchServices filtering.
  //
  // Some flows (e.g. Emergency) use flow-prefixed slugs like "emergency-hvac" that won't
  // match branchServices, so those pills pass through naturally without a special bypass.
  const mainPillOptions = (questionId) => {
    if (!Array.isArray(questions)) return [];
    const allPills = questions
      .filter(
        (q) =>
          normalize(q['Component/Format']) === 'pill' &&
          normalize(q['Parent ID']) === normalize(questionId)
      )
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

    if (!Array.isArray(branchServices) || branchServices.length === 0) {
      return allPills;
    }

    // Always filter pills that have an explicit Service Type against branchServices —
    // this ensures services the branch doesn't offer (e.g. insulation-services) are
    // hidden even when we're inside a derived flow.
    // For pills with no/blank/dash Service Type (flow-internal navigation pills),
    // apply the isInDerivedFlow bypass so they are never incorrectly hidden.
    const parentQuestion = Array.isArray(questions)
      ? questions.find((q) => normalize(q.ID) === normalize(questionId))
      : null;
    const isInDerivedFlow =
      derivedFormId &&
      parentQuestion &&
      normalize(parentQuestion.formId) === normalize(derivedFormId);

    return allPills.filter((pill) => {
      const serviceType = String(pill['Service Type'] || '').trim();
      const hasExplicitServiceType =
        serviceType && serviceType !== '-' && serviceType !== '';
      // Pills with an explicit service type are always filtered by branch services
      if (hasExplicitServiceType) {
        return filterQuestionsByServiceType([pill], branchServices).length > 0;
      }
      // Pills with no service type: pass through inside derived flow, filter outside
      return isInDerivedFlow ? true : true; // no service type → always show
    });
  };

  // Use helper for mainQuestions
  // groupedSort keeps parent groups together: each parent is followed immediately
  // by its children (sorted by order) before the next unrelated parent appears.
  // Passing `questions` (the full universe) lets groupedSort walk up the ancestry
  // chain to find the outermost ancestor's order, so children of pill A (selected
  // on a previous screen) always appear before children of pill B when pill A has
  // a lower order than pill B — even if the children's own order values are swapped.
  const mainQuestions = Array.isArray(screenQuestions)
    ? groupedSort(
        filterQuestionsBySupportedFormats(
          screenQuestions,
          SUPPORTED_FORMATS
        ).filter((q) => {
          const parentId = normalize(q['Parent ID']);
          const hasNoParent = !parentId || parentId === '-';
          const shouldShow = hasNoParent
            ? true
            : selectedPillIdsAllScreens.includes(parentId);
          return shouldShow;
        }),
        questions
      )
    : [];

  // Use helper for childQuestions
  const childQuestions = (parentId) =>
    Array.isArray(questions)
      ? filterQuestionsBySupportedFormats(
          questions.filter(
            (q) => normalize(q['Parent ID']) === normalize(parentId)
          ),
          SUPPORTED_FORMATS
        ).sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      : [];

  const childPillOptions = (parentId) => {
    if (!Array.isArray(questions)) return [];

    const allPills = questions
      .filter(
        (q) =>
          normalize(q['Component/Format']) === 'pill' &&
          normalize(q['Parent ID']) === normalize(parentId)
      )
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

    if (!Array.isArray(branchServices) || branchServices.length === 0) {
      return allPills;
    }

    // Same logic as mainPillOptions: always filter pills with an explicit Service Type
    // against branchServices, even inside a derived flow.
    const parentQuestion = Array.isArray(questions)
      ? questions.find((q) => normalize(q.ID) === normalize(parentId))
      : null;
    const isInDerivedFlow =
      derivedFormId &&
      parentQuestion &&
      normalize(parentQuestion.formId) === normalize(derivedFormId);

    return allPills.filter((pill) => {
      const serviceType = String(pill['Service Type'] || '').trim();
      const hasExplicitServiceType =
        serviceType && serviceType !== '-' && serviceType !== '';
      if (hasExplicitServiceType) {
        return filterQuestionsByServiceType([pill], branchServices).length > 0;
      }
      // No service type → always show (isInDerivedFlow kept for future use)
      return isInDerivedFlow ? true : true;
    });
  };

  const footers = screenQuestions
    .filter((q) => normalize(q['Component/Format']) === 'footer')
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

  // Identify top-level footers: either no parent, or parented to a non-footer
  // question that is visible on this screen (e.g. footers parented to a Question
  // in the Prevmaintenance flow that are meant to always show).
  const screenQuestionIds = new Set(screenQuestions.map((q) => String(q.ID)));
  const topLevelFooters = footers
    .filter((q) => {
      const parentId = q['Parent ID'];
      const hasNoParent =
        !parentId || parentId === '-' || normalize(parentId) === null;
      if (hasNoParent) return true;
      // Also treat as top-level if parent is a non-footer question on this screen
      const parentOnScreen = screenQuestionIds.has(String(parentId));
      if (!parentOnScreen) return false;
      const parentQ = screenQuestions.find(
        (sq) => String(sq.ID) === String(parentId)
      );
      const parentFormat = normalize(parentQ?.['Component/Format']);
      return parentFormat !== 'footer';
    })
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

  // Helper to detect likely phone/contact footer (digits + keyword)
  const isLikelyPhoneParent = (f) => {
    const raw = `${f.Text || ''} ${f['Sub Text/Selection '] || ''}`;
    const digitsLen = (raw.match(/\d/g) || []).length;
    const hasKeyword = /call|phone|contact|help|schedule/i.test(raw);
    return digitsLen >= 7 && hasKeyword;
  };

  // topLevelFooters is already sorted by order above
  const orderedTopLevelFooters = topLevelFooters;

  // Extract DNI-swapped phone number from main page and apply to modal
  useEffect(() => {
    const applyDNIPhone = () => {
      const dniPhone = extractDNIPhoneFromPage();
      if (dniPhone) {
        dniPhoneNumberRef.current = dniPhone;
        // Force re-render to apply DNI phone to footer
        setSTResponseObj((prev) => ({ ...prev, _dniUpdate: Date.now() }));
        return true;
      }
      return false;
    };

    // Try immediately
    const immediate = applyDNIPhone();

    // If not found, try again after delays (in case DNI is still processing)
    if (!immediate) {
      const timers = [
        setTimeout(applyDNIPhone, 500),
        setTimeout(applyDNIPhone, 1000),
        setTimeout(applyDNIPhone, 2000),
      ];

      return () => timers.forEach((t) => clearTimeout(t));
    }
  }, []);

  const canGoBack = screenNumber > 1;

  const allMainRequiredPillsSelected = mainQuestions
    .filter((q) => mainPillOptions(q.ID).length > 0 && isRequired(q))
    .every((q) => {
      const multi = isMultiSelect(q);
      const val = selectedOptionId[q.ID];
      return multi ? Array.isArray(val) && val.length > 0 : !!val;
    });

  const allChildRequiredPillsSelected = mainQuestions
    .flatMap((mainQ) => childQuestions(mainQ.ID))
    .filter(
      (childQ) => childPillOptions(childQ.ID).length > 0 && isRequired(childQ)
    )
    .every((childQ) => {
      const multi = isMultiSelect(childQ);
      const val = childSelectedOptionIds[childQ.ID];
      return multi ? Array.isArray(val) && val.length > 0 : !!val;
    });

  const allRequiredInputsFilled = [
    ...mainQuestions,
    ...mainQuestions.flatMap((mainQ) => childQuestions(mainQ.ID)),
  ]
    .filter(
      (q) =>
        (normalize(q['Component/Format']) === 'dropdown' ||
          normalize(q['Component/Format']) === 'textinput' ||
          normalize(q['Component/Format']) === 'checkbox' ||
          normalize(q['Component/Format']) === 'disclosure') &&
        isRequired(q)
    )
    .every(
      (q) => inputAnswers[q.ID] !== undefined && inputAnswers[q.ID] !== false
    );

  const hasRequired =
    mainQuestions.some(
      (q) => mainPillOptions(q.ID).length > 0 && isRequired(q)
    ) ||
    mainQuestions
      .flatMap((mainQ) => childQuestions(mainQ.ID))
      .some(
        (childQ) => childPillOptions(childQ.ID).length > 0 && isRequired(childQ)
      ) ||
    [
      ...mainQuestions,
      ...mainQuestions.flatMap((mainQ) => childQuestions(mainQ.ID)),
    ].some(
      (q) =>
        (normalize(q['Component/Format']) === 'dropdown' ||
          normalize(q['Component/Format']) === 'textinput' ||
          normalize(q['Component/Format']) === 'checkbox' ||
          normalize(q['Component/Format']) === 'disclosure') &&
        isRequired(q)
    );

  // Compute the last reachable screen dynamically based on current selections.
  // Walk forward from the current screen: for each future screen, check whether
  // it contains at least one non-pill question that is reachable given the
  // currently selected pill IDs.  The highest such screen is the effective last.
  const dynamicLastScreen = (() => {
    if (!Array.isArray(questions) || lastScreenNumber === null) {
      return lastScreenNumber;
    }
    // Build the full set of selected pill IDs across all screens (already computed above).
    // Includes both main pill selections (selectedOptionId) and child pill selections
    // (childSelectedOptionIds) so that deeply nested pill chains (e.g. Screen 3 child
    // pills whose children live on Screen 4) are correctly counted as reachable.
    const selPillIds = [
      ...selectedPillIdsAllScreens,
      ...selectedChildPillIdsAllScreens,
    ];

    let highestReachable = screenNumber; // at minimum the current screen is reachable

    for (let sn = screenNumber + 1; sn <= lastScreenNumber; sn++) {
      const snQuestions = questions.filter(
        (q) => Number(q['Screen Number']) === sn
      );
      if (snQuestions.length === 0) continue;

      // A question on this screen is "reachable" when it either has no parent
      // (top-level) or its parent pill is in the selected set.
      // Footers are excluded — they appear on every screen and must not
      // make an otherwise-empty screen look reachable.
      const hasReachable = snQuestions.some((q) => {
        const fmt = normalize(q['Component/Format']);
        if (fmt === 'pill' || fmt === 'footer') return false; // never count these
        const parentId = normalize(q['Parent ID']);
        if (!parentId || parentId === '-') return true; // top-level always reachable
        return selPillIds.includes(parentId); // child of a selected pill
      });

      if (hasReachable) {
        highestReachable = sn;
      }
    }

    return highestReachable;
  })();

  const isLastScreen =
    lastScreenNumber !== null && screenNumber >= dynamicLastScreen;

  // Added: show error message if questions have loaded but none are available for this screen
  const isLoaded = questions !== null;
  if (isLoaded && screenQuestions.length === 0) {
    return (
      <div className="diagnostic-container">
        <LogoComponent src={logoUrl} alt="NearU Logo" />
        <div
          className="no-questions-error"
          style={{
            background: '#ffecec',
            border: '1px solid #f5c2c2',
            color: '#b30000',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1.5rem',
          }}
        >
          We are sorry, but no questions are available for this step. Please go
          back or refresh the page.
        </div>
        <NavigationComponent
          canGoBack={canGoBack}
          canGoNext={false}
          onBack={handleBack}
          onNext={handleNext}
          hideNext={true}
        />
      </div>
    );
  }

  const hasRequiredInputsOnScreen = screenQuestions
    .filter(
      (q) =>
        (normalize(q['Component/Format']) === 'dropdown' ||
          normalize(q['Component/Format']) === 'textinput' ||
          normalize(q['Component/Format']) === 'checkbox' ||
          normalize(q['Component/Format']) === 'disclosure') &&
        isRequired(q)
    )
    .some((q) => inputAnswers[q.ID] !== true);

  const canGoNext = (() => {
    // Require user selection of formId pill if not yet chosen
    if (screenNumber === 1 && !derivedFormId && formIdQuestionId) return false;
    if (mainQuestions.length === 0) return !hasRequiredInputsOnScreen;

    // Check if there are any main questions with pill options
    const mainQuestionsWithPills = mainQuestions.filter(
      (q) => mainPillOptions(q.ID).length > 0
    );
    // Only include child questions with pills that are explicitly required.
    // Non-required child pill questions (e.g. optional sub-selections like
    // "What type of lighting?") should not block navigation.
    const childQuestionsWithPills = mainQuestions
      .flatMap((mainQ) => childQuestions(mainQ.ID))
      .filter(
        (childQ) => childPillOptions(childQ.ID).length > 0 && isRequired(childQ)
      );

    const hasPillOptions =
      mainQuestionsWithPills.length > 0 || childQuestionsWithPills.length > 0;

    if (hasRequired) {
      return (
        allMainRequiredPillsSelected &&
        allChildRequiredPillsSelected &&
        allRequiredInputsFilled
      );
    }

    // Check if all main pill questions have selections
    const allMainSelected = mainQuestionsWithPills.every((q) => {
      const multi = isMultiSelect(q);
      const val = selectedOptionId[q.ID];
      // Empty arrays should not count as selected for non-multi questions
      return multi
        ? Array.isArray(val) && val.length > 0
        : !!val && (!Array.isArray(val) || val.length > 0);
    });

    // Check if all child pill questions have selections
    const allChildSelected = childQuestionsWithPills.every((childQ) => {
      const multi = isMultiSelect(childQ);
      const val = childSelectedOptionIds[childQ.ID];
      // Empty arrays should not count as selected for non-multi questions
      return multi
        ? Array.isArray(val) && val.length > 0
        : !!val && (!Array.isArray(val) || val.length > 0);
    });

    // If there are no pills on this screen, allow next if inputs are filled
    if (!hasPillOptions) {
      return allRequiredInputsFilled;
    }

    // If there are pills, require at least one selection (either main or child)
    const hasAnyPillSelection =
      (mainQuestionsWithPills.length > 0 && allMainSelected) ||
      (childQuestionsWithPills.length > 0 && allChildSelected);

    return hasAnyPillSelection && allRequiredInputsFilled;
  })();

  // Restored navigation handlers
  function handleBack(e) {
    if (screenNumber > 1) {
      // Identify all question IDs (main and child) for the current screen
      const currentScreenQuestions = Array.isArray(questions)
        ? questions.filter((q) => Number(q['Screen Number']) === screenNumber)
        : [];
      const mainQuestionIds = currentScreenQuestions.map((q) => q.ID);
      const childQuestionIds = currentScreenQuestions
        .flatMap((mainQ) =>
          Array.isArray(questions)
            ? questions.filter(
                (q) => normalize(q['Parent ID']) === normalize(mainQ.ID)
              )
            : []
        )
        .map((q) => q.ID);
      // Clear selections for these questions
      setSelectedOptionId((prev) => {
        const updated = { ...prev };
        mainQuestionIds.forEach((id) => delete updated[id]);
        return updated;
      });
      setChildSelectedOptionIds((prev) => {
        const updated = { ...prev };
        childQuestionIds.forEach((id) => delete updated[id]);
        return updated;
      });
      setInputAnswers((prev) => {
        const updated = { ...prev };
        [...mainQuestionIds, ...childQuestionIds].forEach(
          (id) => delete updated[id]
        );
        return updated;
      });
      const prevScreenNumber = screenNumber - 1;
      const prevScreenQuestions = Array.isArray(questions)
        ? questions.filter(
            (q) => Number(q['Screen Number']) === prevScreenNumber
          )
        : [];
      const prevMainQuestions = prevScreenQuestions.filter((q) => {
        const format = normalize(q['Component/Format']);
        const parentId = normalize(q['Parent ID']);
        return (
          !parentId ||
          parentId === '-' ||
          format === 'header' ||
          format === 'question' ||
          format === 'dropdown' ||
          format === 'textinput' ||
          format === 'checkbox' ||
          format === 'subheader' ||
          format === 'disclosure' ||
          format === 'bullet' ||
          format === 'text'
        );
      });
      // Only clear inputAnswers for non-pill questions
      const idsToClear = [
        ...prevMainQuestions
          .filter((q) => {
            const format = normalize(q['Component/Format']);
            return (
              format === 'dropdown' ||
              format === 'textinput' ||
              format === 'checkbox' ||
              format === 'disclosure'
            );
          })
          .map((q) => q.ID),
        ...prevMainQuestions
          .flatMap((mainQ) =>
            prevScreenQuestions.filter(
              (q) => normalize(q['Parent ID']) === normalize(mainQ.ID)
            )
          )
          .filter((q) => {
            const format = normalize(q['Component/Format']);
            return (
              format === 'dropdown' ||
              format === 'textinput' ||
              format === 'checkbox' ||
              format === 'disclosure'
            );
          })
          .map((childQ) => childQ.ID),
      ];
      setInputAnswers((prev) => {
        const updated = { ...prev };
        idsToClear.forEach((id) => delete updated[id]);
        return updated;
      });
      setScreenNumber(prevScreenNumber);
    }
  }

  function handleNext(e) {
    if (screenNumber < dynamicLastScreen) {
      commitBuffered('next-intermediate');
      setScreenNumber((prev) => prev + 1);
      setChildSelectedOptionIds({});
      setInputAnswers({});
    } else if (screenNumber >= dynamicLastScreen) {
      commitBuffered('next-final');
      // Fire handoff event before opening scheduler
      pushSchedulerHandoffST({
        scheduler_name: formType,
        entry_key: getEntryKey(propEntryName || ''),
        flow_id: flowId,
        entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
        questionText: 'Booking Handoff',
      });
      if (window._scheduler && typeof window._scheduler.show === 'function') {
        const simpleAnswersObj = window.__stSimpleAnswers || {};

        // Build the exact set of Service Type keys the user selected by looking
        // at every selected pill ID (across all screens) and reading its
        // 'Service Type' field directly from the questions array.
        // This is an exact lookup — no fuzzy/substring matching.
        const allSelectedPillIds = new Set([
          ...Object.values(selectedOptionId).flat().filter(Boolean).map(String),
          ...Object.values(childSelectedOptionIds)
            .flat()
            .filter(Boolean)
            .map(String),
        ]);
        const selectedServiceTypeKeys = new Set();
        questions.forEach((q) => {
          if (
            normalize(q['Component/Format']) === 'pill' &&
            allSelectedPillIds.has(String(q.ID)) &&
            q['Service Type']
          ) {
            // A pill can have comma-delimited service types
            String(q['Service Type'])
              .split(',')
              .forEach((st) => {
                const trimmed = st.trim();
                if (trimmed) selectedServiceTypeKeys.add(trimmed);
              });
          }
        });

        // For every pricing question whose Service Type key was selected,
        // look up the live price from branchMetaObj and write it to the summary.
        const pricingQuestions = questions.filter(
          (q) =>
            normalize(q['Component/Format']) === 'pricing' && q['Service Type']
        );
        pricingQuestions.forEach((q) => {
          const serviceTypeKey = q['Service Type'];
          if (!selectedServiceTypeKeys.has(serviceTypeKey)) return;

          let pricingValue = '';
          if (branchMetaObj) {
            const pricingInfo = getPricingInfo(branchMetaObj, serviceTypeKey);
            if (pricingInfo && pricingInfo.fixedPrice) {
              pricingValue = pricingInfo.fixedPrice;
            } else if (
              pricingInfo &&
              pricingInfo.priceLow &&
              pricingInfo.priceHigh
            ) {
              pricingValue = `${formatUSD(parseFloat(pricingInfo.priceLow))} - ${formatUSD(parseFloat(pricingInfo.priceHigh))}`;
            }
          }
          simpleAnswersObj[serviceTypeKey] = pricingValue;
        });

        const summaryString = JSON.stringify(simpleAnswersObj);
        stSummaryRef.current = summaryString;
        window._stPreBookingSummary = summaryString;
        const schedulerId = window.QuestionnaireFormData?.schedulerId || '';
        console.log(
          '[ST Payload] Scheduler opening — pre-booking summary snapshot:',
          {
            simpleAnswers: simpleAnswersObj,
            summaryString,
            schedulerId,
            fullSTResponseObj: window.__stResponseObj,
          }
        );
        if (!schedulerId) {
          console.error(
            '[ST Payload] No schedulerId found in QuestionnaireFormData — scheduler will not open correctly. Set nearu_scheduler_id in WordPress options.'
          );
        }
        window._scheduler.show({
          schedulerId,
        });
      }
    }
  }
  // commitBuffered helper reintroduced
  const commitBuffered = (tag) => {
    const snapshot = getBufferSnapshot();
    if (snapshot.length) {
      snapshot.forEach((payload) => {
        window.__stResponseObj = updateServiceTitanResponse(
          window.__stResponseObj,
          payload
        );
        stResponseRef.current = window.__stResponseObj;
      });
      clearStEventBuffer();
      console.log(
        `[ST Payload] commitBuffered (${tag}) — events flushed to __stResponseObj:`,
        {
          flushedCount: snapshot.length,
          flushedEvents: snapshot,
          fullSTResponseObj: window.__stResponseObj,
        }
      );
    }
  };

  // Added: simple answers accumulator used for ServiceTitan summary
  const updateSimpleAnswers = (questionText, value) => {
    try {
      const key = (questionText || '').trim();
      if (!key) return;
      if (!window.__stSimpleAnswers) window.__stSimpleAnswers = {};
      // If multi-select provides an array, store array, else store scalar string
      const storedValue = Array.isArray(value)
        ? value.filter((v) => v != null && String(v).trim() !== '')
        : value;
      window.__stSimpleAnswers[key] = storedValue;
    } catch (e) {
      // silent
    }
  };

  // Update interaction handlers to use addToBuffer
  const handleMainPillSelect = (mainQId, pillId, e) => {
    const question = questions.find((q) => q.ID === mainQId);
    const multi = isPillMultiSelect(questions.find((q) => q.ID === pillId));
    const elementInfo = getElementInfo(e?.target);
    const pillObj = questions.find((q) => q.ID === pillId);
    if (
      pillObj?.Text &&
      (pillObj.Text.toLowerCase().includes('commercial') ||
        pillObj.Text.toLowerCase().includes('residential'))
    ) {
      setSelectedPropertyType(pillObj.Text.toLowerCase());
    }
    const prevVal = selectedOptionId[mainQId];
    const finalValue = togglePillValue(prevVal, pillId, multi);
    const wasSelected = multi
      ? Array.isArray(prevVal) && prevVal.map(String).includes(String(pillId))
      : String(prevVal) === String(pillId);
    setSelectedOptionId((prev) => ({
      ...prev,
      [mainQId]: multi
        ? Array.isArray(finalValue)
          ? finalValue
          : []
        : finalValue,
    }));
    const selectedLabelText = pillObj ? getQuestionText(pillObj) : '';
    const lowerSel = (selectedLabelText || '').trim().toLowerCase();
    if (['hvac', 'electrical', 'plumbing', 'generator'].includes(lowerSel)) {
      setDerivedFormId(selectedLabelText.trim());
    }
    const questionText = getQuestionText(question);

    // Fire GTM event for this specific pill interaction (always fire, even for deselection)
    const eventValue = wasSelected ? `deselect:${pillId}` : `select:${pillId}`;
    pushSelectionEvent({
      event: 'pill_select',
      value: String(pillId || ''),
      selectedLabel: selectedLabelText || '',
      questionText: questionText || '',
      scheduler_name: formType,
      elementTag: 'button',
      flow_id: flowId || '',
      entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
      entry_key: getEntryKey(propEntryName || ''),
      step_index: screenNumber,
      question_id: mainQId,
      question_text: questionText || '',
      answer_value: String(pillId || ''),
      answer_label: selectedLabelText || '',
    });

    // Pricing info extraction (refactored to helper)
    const { pricingDisplay, pricingKeyUsed, pricingInfo, pricingLabel } =
      getPricingDisplayInfo(
        questions,
        pillId,
        branchMetaObj,
        getQuestionText,
        normalize,
        getPricingInfo
      );
    // Use pricingLabel for selectedLabel if pricing child exists, else use selectedLabelText
    const finalSelectedLabel = pricingLabel || selectedLabelText;

    setResponseObj((prev) =>
      updateGTMResponse(prev, {
        questionId: mainQId,
        value: finalValue,
        questionText,
        type: 'Pill',
        selectedLabel: finalSelectedLabel,
        questionObj: question,
        event: 'PillSelect',
        ...elementInfo,
        pagePostType: 'frontpage',
        pagePostType2: 'single-page',
        pagePostAuthor: 'nearu',
        schedulerName: formType,
        pricing: pricingDisplay,
        flow_id: flowId || '',
      })
    );
    const payload = {
      questionId: mainQId,
      value: finalValue,
      questionText,
      type: 'Pill',
      selectedLabel: finalSelectedLabel,
      questionObj: question,
      event: 'PillSelect',
      ...elementInfo,
      pagePostType: 'frontpage',
      pagePostType2: 'single-page',
      pagePostAuthor: 'nearu',
      schedulerName: formType,
      pricing: pricingDisplay,
    };
    // Add to ServiceTitan response (ST events)
    setSTResponseObj((prev) => {
      const updated = updateServiceTitanResponse(prev, payload);
      return updated;
    });
    addToBuffer(payload);
    if (multi) {
      const pillOpts = mainPillOptions(mainQId);
      const selIds = Array.isArray(finalValue) ? finalValue.map(coerceId) : [];
      const labels = pillOpts
        .filter((po) => selIds.includes(coerceId(po.ID)))
        .map((po) => getQuestionText(po));
      updateSimpleAnswers(questionText, labels);
    } else {
      updateSimpleAnswers(questionText, selectedLabelText);
    }
    if (!multi) {
      const childQs = childQuestions(mainQId);
      setChildSelectedOptionIds((prev) => {
        const updated = { ...prev };
        childQs.forEach((child) => {
          delete updated[child.ID];
        });
        return updated;
      });
    }
  };

  const handleChildPillSelect = (childQId, pillId, e) => {
    const question = questions.find((q) => q.ID === childQId);
    const multi = isPillMultiSelect(question);
    const elementInfo = getElementInfo(e?.target);
    const pillObj = questions.find((q) => q.ID === pillId);
    if (
      pillObj?.Text &&
      (pillObj.Text.toLowerCase().includes('commercial') ||
        pillObj.Text.toLowerCase().includes('residential'))
    ) {
      setSelectedPropertyType(pillObj.Text.toLowerCase());
    }
    const prevVal = childSelectedOptionIds[childQId];
    const finalValue = togglePillValue(prevVal, pillId, multi);
    setChildSelectedOptionIds((prev) => ({ ...prev, [childQId]: finalValue }));
    const selectedLabelText = pillObj ? getQuestionText(pillObj) : '';
    const lowerSel = (selectedLabelText || '').trim().toLowerCase();
    if (['hvac', 'electrical', 'plumbing', 'generator'].includes(lowerSel)) {
      setDerivedFormId(selectedLabelText.trim());
    }
    const questionText = getQuestionText(question);

    // Fire GTM event for this specific pill interaction (always fire, even for deselection)
    pushSelectionEvent({
      event: 'child_pill_select',
      value: String(pillId || ''),
      selectedLabel: selectedLabelText || '',
      questionText: questionText || '',
      scheduler_name: formType,
      elementTag: 'button',
      flow_id: flowId || '',
      entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
      entry_key: getEntryKey(propEntryName || ''),
      step_index: screenNumber,
      question_id: childQId,
      question_text: questionText || '',
      answer_value: String(pillId || ''),
      answer_label: selectedLabelText || '',
    });
  };

  const handleInputChange = (questionId, value, e) => {
    const question = questions.find((q) => q.ID === questionId);
    const elementInfo = getElementInfo(e?.target);
    const questionText = getQuestionText(question);
    const typeFormat = normalize(question?.['Component/Format']);
    setInputAnswers((prev) => ({ ...prev, [questionId]: value }));
    const payload = {
      questionId,
      value,
      questionText,
      type: typeFormat,
      questionObj: question,
      event: 'InputChange',
      ...elementInfo,
      pagePostType: 'frontpage',
      pagePostType2: 'single-page',
      pagePostAuthor: 'nearu',
      schedulerName: formType,
    };
    addToBuffer(payload);
    updateSimpleAnswers(questionText, value);
    pushSelectionEvent({
      event: 'input_change',
      value: String(value ?? ''),
      selectedLabel: '',
      questionText: questionText || '',
      scheduler_name: formType,
      elementTag: (e?.target?.tagName || 'input').toLowerCase(),
      flow_id: flowId || '',
      entry_label: propEntryName || 'ENTRY_LABEL_PLACEHOLDER',
      entry_key: getEntryKey(propEntryName || ''),
      step_index: screenNumber,
      question_id: questionId,
      question_text: questionText || '',
      answer_value: String(value ?? ''),
      answer_label: String(value ?? ''),
    });
  };

  const branchMetaObj =
    Array.isArray(branchMetadata) && branchMetadata.length > 0
      ? branchMetadata[0]
      : {};

  if (mainQuestions.length === 0) {
    const infoItems = screenQuestions.filter(
      (q) =>
        normalize(q['Component/Format']) === 'text' ||
        normalize(q['Component/Format']) === 'bullet' ||
        normalize(q['Component/Format']) === 'pricing'
    );

    const grouped = {};
    infoItems.forEach((item) => {
      const group =
        normalize(item['Troubleshoot/DIY OR Professional Repair']) || 'Other';
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(item);
    });

    return (
      <div className="diagnostic-container">
        <LogoComponent src={logoUrl} alt="NearU Logo" />

        {Object.keys(grouped).map((group) => (
          <div key={group} style={{ marginBottom: '2em' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: '1.1em',
                marginBottom: '0.5em',
              }}
            >
              {group}
            </div>
            {grouped[group].map((item) => {
              const fmt = normalize(item['Component/Format']);
              if (fmt === 'pricing') {
                const parentId = String(item['Parent ID']);
                const itemParentQuestion =
                  grouped[group].find((q) => String(q.ID) === parentId) || null;
                return (
                  <ErrorBoundary key={item.ID}>
                    <PricingEstimateBox
                      question={item}
                      branchMetaObj={branchMetaObj}
                      parentQuestion={itemParentQuestion}
                    />
                  </ErrorBoundary>
                );
              }
              if (fmt === 'text') {
                return <TextComponent key={item.ID} question={item} />;
              }
              if (fmt === 'bullet') {
                const parentId = String(item['Parent ID']);
                const parentQuestion =
                  grouped[group].find((q) => String(q.ID) === parentId) || null;
                return (
                  <BulletComponent
                    key={item.ID}
                    question={item}
                    parentQuestion={parentQuestion}
                    branchMetadata={branchMetaObj}
                  />
                );
              }
              return null;
            })}
          </div>
        ))}
        <NavigationComponent
          canGoBack={canGoBack}
          canGoNext={canGoNext}
          onBack={handleBack}
          onNext={handleNext}
          hideNext={isLastScreen}
        />
      </div>
    );
  }

  if (loadingQuestions) {
    return (
      <div className="diagnostic-container">
        <LogoComponent src={logoUrl} alt="NearU Logo" />
        <div style={{ padding: '1rem', fontWeight: 600 }}>
          Loading questions...
        </div>
      </div>
    );
  }

  return (
    <div className="diagnostic-container">
      <LogoComponent src={logoUrl} alt="NearU Logo" />

      {mainQuestions.map((mainQuestion) => {
        const multi = isMultiSelect(mainQuestion);
        const selected = selectedOptionId[mainQuestion.ID];
        const format = normalize(mainQuestion['Component/Format']);
        const pillOpts = mainPillOptions(mainQuestion.ID);
        const groupClass = `pill-options-group${pillOpts.length === 1 ? ' single-pill' : ''}${pillOpts.length === 2 ? ' two-pills' : ''}`;

        if (format === 'separator') {
          return <SeparatorComponent key={mainQuestion.ID} />;
        }

        if (format === 'header') {
          return (
            <div key={mainQuestion.ID} className="question-block-wrapper">
              <HeaderComponent question={mainQuestion} />
              {pillOpts.length > 0 && (
                <div className={groupClass}>
                  {pillOpts.map((option) => {
                    const multiCombined = isPillMultiSelect(option);
                    let selectedRaw = selectedOptionId[mainQuestion.ID];
                    if (multiCombined && !Array.isArray(selectedRaw)) {
                      selectedRaw = [];
                    }
                    let isSelected;
                    if (multiCombined) {
                      isSelected = selectedRaw
                        .map(coerceId)
                        .includes(coerceId(option.ID));
                    } else {
                      isSelected =
                        coerceId(selectedRaw) === coerceId(option.ID);
                    }
                    return (
                      <button
                        key={option.ID}
                        className={`pill-btn${isSelected ? ' selected' : ''}`}
                        onClick={(e) => {
                          handleMainPillSelect(mainQuestion.ID, option.ID, e);
                          if (e && e.target) e.target.blur();
                        }}
                        type="button"
                      >
                        <PillComponent
                          question={option}
                          label={getQuestionText(option)}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
              {renderChildren({
                parentId: mainQuestion.ID,
                questions,
                selectedOptionId,
                childSelectedOptionIds,
                inputAnswers,
                handleMainPillSelect,
                handleChildPillSelect,
                handleInputChange,
                pillOptionsFn: mainPillOptions,
                childPillOptionsFn: childPillOptions,
                childQuestionsFn: childQuestions,
                branchMetaObj,
                parentQuestion: mainQuestion,
              })}
            </div>
          );
        }

        if (format === 'dropdown') {
          return (
            <div key={mainQuestion.ID} className="question-block-wrapper">
              <DropdownComponent
                question={mainQuestion}
                value={inputAnswers[mainQuestion.ID] || ''}
                onChange={(val, e) =>
                  handleInputChange(mainQuestion.ID, val, e)
                }
              />
            </div>
          );
        }
        if (format === 'textinput') {
          return (
            <div key={mainQuestion.ID} className="question-block-wrapper">
              <TextInputComponent
                question={mainQuestion}
                value={inputAnswers[mainQuestion.ID] || ''}
                onChange={(val, e) =>
                  handleInputChange(mainQuestion.ID, val, e)
                }
              />
            </div>
          );
        }
        if (format === 'checkbox') {
          return (
            <div key={mainQuestion.ID} className="question-block-wrapper">
              <CheckboxComponent
                question={mainQuestion}
                checked={!!inputAnswers[mainQuestion.ID]}
                onChange={(val, e) =>
                  handleInputChange(mainQuestion.ID, val, e)
                }
              />
            </div>
          );
        }
        if (format === 'disclosure') {
          return (
            <div key={mainQuestion.ID} className="question-block-wrapper">
              <DisclosureComponent
                question={mainQuestion}
                checked={!!inputAnswers[mainQuestion.ID]}
                onChange={(val, e) =>
                  handleInputChange(mainQuestion.ID, val, e)
                }
              />
            </div>
          );
        }
        if (format === 'pricing') {
          return (
            <ErrorBoundary key={mainQuestion.ID}>
              <PricingEstimateBox
                question={mainQuestion}
                branchMetaObj={branchMetaObj}
                parentQuestion={null}
              />
            </ErrorBoundary>
          );
        }
        if (format === 'text') {
          return (
            <TextComponent key={mainQuestion.ID} question={mainQuestion} />
          );
        }
        if (format === 'bullet') {
          const parentObj = findParentQuestion(questions, mainQuestion);
          return (
            <BulletComponent
              key={mainQuestion.ID}
              question={mainQuestion}
              parentQuestion={parentObj}
              branchMetadata={branchMetaObj}
            />
          );
        }
        if (format === 'subheader') {
          return (
            <SubheaderComponent key={mainQuestion.ID} question={mainQuestion}>
              {renderChildren({
                parentId: mainQuestion.ID,
                questions,
                selectedOptionId,
                childSelectedOptionIds,
                inputAnswers,
                handleMainPillSelect,
                handleChildPillSelect,
                handleInputChange,
                pillOptionsFn: mainPillOptions,
                childPillOptionsFn: childPillOptions,
                childQuestionsFn: childQuestions,
                depth: 1,
                branchMetaObj,
                parentQuestion: mainQuestion, // pass parent to children
              })}
            </SubheaderComponent>
          );
        }
        if (format === 'question') {
          return (
            <QuestionComponent
              key={mainQuestion.ID}
              question={mainQuestion}
              depth={0}
            >
              {pillOpts.length > 0 && (
                <div className={groupClass}>
                  {pillOpts.map((option) => {
                    const multiCombined = isPillMultiSelect(option);
                    let selectedRaw = selectedOptionId[mainQuestion.ID];
                    if (multiCombined && !Array.isArray(selectedRaw)) {
                      selectedRaw = [];
                    }
                    let isSelected;
                    if (multiCombined) {
                      isSelected = selectedRaw
                        .map(coerceId)
                        .includes(coerceId(option.ID));
                    } else {
                      isSelected =
                        coerceId(selectedRaw) === coerceId(option.ID);
                    }
                    return (
                      <button
                        key={option.ID}
                        className={`pill-btn${isSelected ? ' selected' : ''}`}
                        onClick={(e) => {
                          handleMainPillSelect(mainQuestion.ID, option.ID, e);
                          if (e && e.target) e.target.blur();
                        }}
                        type="button"
                      >
                        <PillComponent
                          question={option}
                          label={getQuestionText(option)}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
              {renderChildren({
                parentId: mainQuestion.ID,
                questions,
                selectedOptionId,
                childSelectedOptionIds,
                inputAnswers,
                handleMainPillSelect,
                handleChildPillSelect,
                handleInputChange,
                pillOptionsFn: mainPillOptions,
                childPillOptionsFn: childPillOptions,
                childQuestionsFn: childQuestions,
                depth: 1,
                branchMetaObj,
                parentQuestion: mainQuestion, // pass parent to children
              })}
            </QuestionComponent>
          );
        }
        return null;
      })}

      {orderedTopLevelFooters.map((parentFooter) => {
        const childList = footers.filter(
          (childFooter) =>
            normalize(childFooter['Parent ID']) === normalize(parentFooter.ID)
        );
        const phoneLike = isLikelyPhoneParent(parentFooter);
        return (
          <React.Fragment key={parentFooter.ID}>
            {phoneLike &&
              childList.map((childFooter) => (
                <FooterComponent
                  key={childFooter.ID}
                  question={childFooter}
                  branchMetadata={branchMetaObj}
                  isChild={true}
                  dniPhoneNumber={dniPhoneNumberRef.current}
                />
              ))}
            <FooterComponent
              question={parentFooter}
              branchMetadata={branchMetaObj}
              isChild={false}
              dniPhoneNumber={dniPhoneNumberRef.current}
            />
            {!phoneLike &&
              childList.map((childFooter) => (
                <FooterComponent
                  key={childFooter.ID}
                  question={childFooter}
                  branchMetadata={branchMetaObj}
                  isChild={true}
                  dniPhoneNumber={dniPhoneNumberRef.current}
                />
              ))}
          </React.Fragment>
        );
      })}

      <NavigationComponent
        canGoBack={canGoBack}
        canGoNext={canGoNext}
        onBack={handleBack}
        onNext={handleNext}
        hideNext={isLastScreen}
      />
    </div>
  );
}

export default EnterpriseTool;
